import { Link } from 'react-router-dom'
import { RailSection } from 'components/PageShell'
import { getMatchRoomRoute, ROUTES } from 'settings/constants'
import { useUpcomingMatches } from './useUpcomingMatches'
import type { NextLeagueMatch } from 'views/Hub/data/useNextLeagueMatch'

const statusMeta = (status: NextLeagueMatch['status']) => {
  switch (status) {
    case 'live':
      return { color: '#ff3b5c', pulse: true, label: 'LIVE' }
    case 'disputed':
      return { color: '#ff3b5c', pulse: false, label: 'DISPUTED' }
    case 'unscheduled':
      return { color: '#8a8a8a', pulse: false, label: 'TBD' }
    case 'forfeit':
      return { color: '#8a8a8a', pulse: false, label: 'FORFEIT' }
    case 'scheduled':
    default:
      return { color: '#00ff94', pulse: false, label: 'SCHEDULED' }
  }
}

const formatDateChip = (
  iso?: string | null,
): { top: string; bottom: string } | null => {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return {
    top: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    bottom: d.getDate().toString(),
  }
}

const formatTimeLabel = (iso?: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const isTomorrow = d.toDateString() === tomorrow.toDateString()
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
  if (sameDay) return `Today · ${time}`
  if (isTomorrow) return `Tomorrow · ${time}`
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} · ${time}`
}

/**
 * Compact list of the authenticated user's upcoming league matches.
 *
 * Modeled on Hub's UpcomingEventsList — date chip on the left, opponent + league
 * + status on the right, single Link per row.  Replaces the 309-line
 * UpcomingMatchesWidget without losing the adaptive polling cadence.
 */
export const UpcomingMatchesList = () => {
  const { matches, loading } = useUpcomingMatches(3)
  if (loading && matches.length === 0) return null
  if (!matches.length) return null

  return (
    <RailSection
      title="Upcoming Matches"
      viewAllHref={ROUTES.League}
      variant="bare"
    >
      <ul
        className="rounded-xl overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, rgba(10,13,15,0.55) 0%, rgba(5,7,9,0.55) 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.07)',
        }}
      >
        {matches.map((m, i) => {
          const status = statusMeta(m.status)
          const chip = formatDateChip(m.scheduledTime)
          const timeLabel = formatTimeLabel(m.scheduledTime)
          const opponentName = m.opponent?.team_name || 'TBD'
          const href = m._id
            ? getMatchRoomRoute(String(m._id))
            : m.leagueId
            ? `/leagues/${m.leagueId}`
            : ROUTES.League

          return (
            <li
              key={`${m._id}-${i}`}
              className={
                i < matches.length - 1
                  ? 'border-b border-white/[0.04]'
                  : undefined
              }
            >
              <Link
                to={href}
                className="group relative flex items-stretch gap-3 px-3.5 py-2.5 hover:bg-white/[0.025] transition-colors"
              >
                <div className="flex flex-col items-center justify-center min-w-[36px]">
                  {chip ? (
                    <>
                      <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#02f305]/75">
                        {chip.top}
                      </span>
                      <span
                        className="text-[1.4rem] font-bold text-white leading-none tabular-nums"
                        style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {chip.bottom}
                      </span>
                    </>
                  ) : (
                    <span
                      className="text-[1.05rem] font-bold text-white/55 leading-none"
                      style={{
                        fontFamily: 'Rajdhani, sans-serif',
                      }}
                    >
                      TBD
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/45 truncate">
                      {m.leagueName}
                    </span>
                    <span
                      className="relative inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.18em] shrink-0"
                      style={{ color: status.color }}
                    >
                      <span
                        className="relative inline-flex w-1.5 h-1.5"
                        aria-hidden
                      >
                        {status.pulse && (
                          <span
                            className="absolute inset-0 rounded-full animate-ping opacity-60"
                            style={{ background: status.color }}
                          />
                        )}
                        <span
                          className="relative w-1.5 h-1.5 rounded-full"
                          style={{ background: status.color }}
                        />
                      </span>
                      {status.label}
                    </span>
                  </div>
                  <p
                    className="mt-0.5 text-[12px] font-bold text-white uppercase truncate"
                    style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      letterSpacing: '0',
                    }}
                  >
                    vs {opponentName}
                  </p>
                  {timeLabel && (
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/40 truncate">
                      {timeLabel}
                    </p>
                  )}
                  {m.actionRequired && !timeLabel && (
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#00ff94]/90 truncate">
                      {m.actionRequired}
                    </p>
                  )}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </RailSection>
  )
}
